import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { Box, Typography, Button, TextField } from '@mui/material';

import { primaryPillButtonSx, destructivePillButtonSx, drawerActionButtonSx } from '../../../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
import { opsColors } from '../../../../theme/opsTokens';

interface NumberOfPassengersEditorProps {
  numberOfPassengers: any[];
  setNumberOfPassengers: React.Dispatch<React.SetStateAction<any[]>>;
}

const NumberOfPassengersEditor: React.FC<NumberOfPassengersEditorProps> = ({ numberOfPassengers, setNumberOfPassengers }) => {
  const { t } = useTranslation('mapWizards');
  const totalProb = numberOfPassengers.reduce((sum, p) => sum + (p.probability || 0), 0);
  const error = Math.abs(totalProb - 1) > 0.0001;
  const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };
  const parseProbabilityInput = (raw: string | number): number => {
    if (raw === '' || raw === null || raw === undefined) return 0;
    const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
    return Number.isNaN(n) ? 0 : n / 100;
  };
  const handleChange = (idx: number, field: string, value: string | number) => {
    if (field === 'probability' && numberOfPassengers.length === 2) {
      // Auto-correct the other field
      const v = Math.max(0, Math.min(1, parseProbabilityInput(value)));
      setNumberOfPassengers((prev: any[]) => prev.map((p, i) =>
        i === idx ? { ...p, probability: v } : { ...p, probability: 1 - v }
      ));
    } else {
      setNumberOfPassengers((prev: any[]) => prev.map((g, i) =>
        i === idx
          ? {
              ...g,
              [field]: field === 'probability' ? parseProbabilityInput(value) : value,
            }
          : g
      ));
    }
  };
  const addGroup = () => {
    const nextPassengerCount = numberOfPassengers.length > 0 
      ? Math.max(...numberOfPassengers.map(g => g.number_of_passengers)) + 1 
      : 1;
    setNumberOfPassengers((prev: any[]) => [...prev, { number_of_passengers: nextPassengerCount, probability: 0 }]);
  };
  const removeGroup = (idx: number) => setNumberOfPassengers((prev: any[]) => prev.filter((_: any, i: number) => i !== idx));
  return (
    <Box
      sx={(theme) => ({
        mt: 1.5,
        mb: 1.25,
        p: 1.75,
        border: '1px solid',
        borderColor: 'surface.borderSubtle',
        borderRadius: 2.5,
        backgroundColor: alpha(opsColors.neutral.panel, 0.4),
        backdropFilter: 'blur(8px) saturate(110%)',
        WebkitBackdropFilter: 'blur(8px) saturate(110%)',
      })}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('generateRides.passengers.title')}
      </Typography>
      {numberOfPassengers.map((g, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap', py: 1, borderBottom: idx < numberOfPassengers.length - 1 ? '1px solid' : 'none', borderColor: 'surface.borderSubtle' }}>
          <TextField
            label={t('generateRides.passengers.numberOfPassengers')}
            type="number"
            value={g.number_of_passengers}
            onChange={e => handleChange(idx, 'number_of_passengers', Number(e.target.value))}
            onWheel={preventWheelValueChange}
            inputProps={{ min: 1, step: 1 }}
            size="small"
            sx={{ width: { xs: '100%', sm: 180 } }}
          />
          <TextField
            label={t('generateRides.passengers.probability')}
            type="number"
            value={g.probability ? Math.round(g.probability * 100) : ''}
            onChange={e => handleChange(idx, 'probability', e.target.value)}
            onWheel={preventWheelValueChange}
            inputProps={{ min: 0, max: 100, step: 1 }}
            size="small"
            sx={{ width: { xs: '100%', sm: 160 } }}
            error={error}
          />
          {idx > 0 && (
            <Button
              onClick={() => removeGroup(idx)}
              sx={(theme) => ({
                minWidth: 36,
                width: 36,
                ml: { xs: 0, sm: 'auto' },
                ...destructivePillButtonSx(theme),
                ...drawerActionButtonSx,
                px: 0,
              })}
            >
              <Icon name="trash" size={20} />
            </Button>
          )}
        </Box>
      ))}
      <Button
        variant="contained"
        onClick={addGroup}
        sx={(theme) => ({
          mt: 1.25,
          ...primaryPillButtonSx(theme),
          ...drawerActionButtonSx,
          alignSelf: 'flex-start',
        })}
      >
        {t('generateRides.passengers.addGroup')}
      </Button>
      {error && <Typography color="error" sx={{ mt: 0.75 }}>{t('generateRides.passengers.probabilityError')}</Typography>}
    </Box>
  );
};

export default NumberOfPassengersEditor; 
